import React from "react";
import { IconButton, Typography } from "@mui/material";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import KingBedIcon from "@mui/icons-material/KingBed";
import VaccinesIcon from "@mui/icons-material/Vaccines";
import ScienceIcon from "@mui/icons-material/Science";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import CategoryIcon from "@mui/icons-material/Category";
import CrisisAlertIcon from "@mui/icons-material/CrisisAlert";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import { Box } from "@mui/system";

const SIDE_NAV_ITEMS = [
  { label: "OPD", section: "default", icon: LocalHospitalIcon },
  { label: "IPD", section: "ipd", icon: KingBedIcon },
  { label: "Emergency", section: "emergency", icon: CrisisAlertIcon },
  { label: "OT", section: "ot", icon: VaccinesIcon },
  { label: "Lab", section: "lab", icon: ScienceIcon },
  { label: "Radiology", section: "radiology", icon: MonitorHeartIcon },
  { label: "Miscellaneous", section: "Miscellaneous", icon: CategoryIcon },
];

export const ACTIVITY_DRAWER_WIDTH = 288;
const ACTIVITY_TOGGLE_WIDTH = 48;

/**
 * In-layout side panel for OPD / IPD / etc. section switching.
 * It lives inside page flow and scrolls with page content.
 */
const TOGGLE_TAB_HEIGHT = 52;

const sideTabSx = (width, theme) => ({
  width,
  height: TOGGLE_TAB_HEIGHT,
  borderRadius: "0 8px 8px 0",
  borderLeft: "none",
  boxShadow: "2px 0 10px rgba(17, 72, 115, 0.12)",
  ...theme,
});

function SideActionBar({
  onClick,
  fullWidth = false,
  iconSrc,
  label,
  ariaLabel,
  theme = "blue",
}) {
  const themes = {
    blue: {
      border: "1px solid #4F8DF7",
      backgroundColor: "#EAF1FF",
      color: "#1B3FA0",
      boxShadowCollapsed: "2px 0 10px rgba(79, 141, 247, 0.25)",
      boxShadowFull: "inset 0 0 0 9999px rgba(79, 141, 247, 0.04)",
      hoverBackground:
        "linear-gradient(195.29deg, #6B8CFF 10.74%, #1B3FA0 95.14%)",
      hoverBoxShadow: "0px 6px 18px rgba(27, 63, 160, 0.30)",
    },
    teal: {
      border: "1px solid #21cdc0",
      backgroundColor: "#EBFAF9",
      color: "#114873",
      boxShadowCollapsed: "2px 0 10px rgba(17, 72, 115, 0.18)",
      boxShadowFull: "inset 0 0 0 9999px rgba(33, 205, 192, 0.04)",
      hoverBackground:
        "linear-gradient(195.29deg, #64E0D9 10.74%, #114873 95.14%)",
      hoverBoxShadow: "0px 6px 18px rgba(17, 72, 115, 0.25)",
    },
  };
  const palette = themes[theme] || themes.blue;

  return (
    <Box
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      aria-label={ariaLabel}
      sx={{
        display: "flex",
        width: "100%",
        minHeight: TOGGLE_TAB_HEIGHT,
        flexShrink: 0,
        alignItems: "center",
        gap: 1.5,
        px: fullWidth ? 2 : 0,
        justifyContent: fullWidth ? "flex-start" : "center",
        cursor: "pointer",
        border: palette.border,
        borderLeft: "none",
        borderTop: fullWidth ? "none" : undefined,
        borderRight: fullWidth ? palette.border : undefined,
        borderBottom: fullWidth ? palette.border : undefined,
        backgroundColor: palette.backgroundColor,
        color: palette.color,
        boxShadow: fullWidth ? palette.boxShadowFull : palette.boxShadowCollapsed,
        borderRadius: fullWidth ? 0 : "0 8px 8px 0",
        transition: "background 0.3s ease, color 0.3s ease, box-shadow 0.3s ease",
        "&:hover": {
          background: palette.hoverBackground,
          color: "#FFFFFF",
          boxShadow: palette.hoverBoxShadow,
          "& .side-action-icon": {
            filter: "brightness(0) invert(1)",
          },
          "& .side-action-label": {
            color: "#FFFFFF",
          },
        },
      }}
    >
      <Box
        component="img"
        className="side-action-icon"
        src={iconSrc}
        alt=""
        sx={{
          width: 28,
          height: 28,
          flexShrink: 0,
          transition: "filter 0.3s ease",
          ...(fullWidth ? {} : { mx: "auto" }),
        }}
      />
      {fullWidth && (
        <Typography
          className="side-action-label"
          sx={{
            fontWeight: 700,
            fontSize: "15px",
            letterSpacing: "0.3px",
            color: "inherit",
            transition: "color 0.3s ease",
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
}

export default function ActivityLauncherDrawer({
  open,
  onOpen,
  onClose,
  activeSection,
  onSectionChange,
  onPatientManagementClick,
  showAnalytics = false,
  onAnalyticsClick,
  visibleSections,
}) {
  const collapsedWidth = ACTIVITY_TOGGLE_WIDTH;
  const columnWidth = open ? ACTIVITY_DRAWER_WIDTH : collapsedWidth;
  const visibleSet = new Set(visibleSections || []);
  const navItems = SIDE_NAV_ITEMS.filter((item) =>
    visibleSet.has(item.section),
  );

  const handleNavItemClick = (section) => {
    onSectionChange(section);
    onClose();
  };

  return (
    <Box
      sx={{
        width: columnWidth,
        flexShrink: 0,
        position: "relative",
        zIndex: 20,
        pt: { xs: 6, sm: 6 },
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 0.75,
        transition: "width 0.3s ease",
      }}
    >
      <Box sx={{ width: "100%", flexShrink: 0 }}>
      {!open && (
        <IconButton
          onClick={onOpen}
          sx={sideTabSx(collapsedWidth, {
            border: "1px solid #21cdc0",
            backgroundColor: "#EBFAF9",
            color: "#121959",
            "&:hover": {
              background:
                "linear-gradient(195.29deg, #64E0D9 10.74%, #114873 95.14%)",
              color: "#FFFFFF",
            },
          })}
          aria-label="open activity launcher"
        >
          <ChangeCircleIcon sx={{ fontSize: 28 }} />
        </IconButton>
      )}

      {open && (
        <Box
          sx={{
            width: ACTIVITY_DRAWER_WIDTH,
            position: "relative",
            display: "flex",
            flexDirection: "column",
            borderRadius: 0,
            backgroundColor: "#FFFFFF",
            border: "1px solid #21cdc0",
            borderLeft: "none",
            borderTop: "none",
            borderRight: "none",
            boxShadow: "0px 8px 30px rgba(17, 72, 115, 0.18)",
            overflow: "visible",
            zIndex: 21,
          }}
        >
          <IconButton
            onClick={onClose}
            aria-label="close activity launcher from side"
            sx={{
              position: "absolute",
              right: -14,
              top: "50%",
              transform: "translateY(-50%)",
              zIndex: 30,
              width: 28,
              height: 28,
              borderRadius: "50%",
              border: "1px solid #21cdc0",
              backgroundColor: "#FFFFFF",
              color: "#114873",
              boxShadow: "0px 2px 8px rgba(17, 72, 115, 0.18)",
              "&:hover": {
                background:
                  "linear-gradient(195.29deg, #64E0D9 10.74%, #114873 95.14%)",
                color: "#FFFFFF",
              },
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              width: "100%",
              minHeight: 52,
              flexShrink: 0,
              zIndex: 1,
              background:
                "linear-gradient(195.29deg, #64E0D9 10.74%, #114873 95.14%)",
              color: "#FFFFFF",
              borderTop: "1px solid #21cdc0",
              borderBottom: "1px solid #21cdc0",
              borderRight: "1px solid #21cdc0",
            }}
          >
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                px: 2,
                fontWeight: 700,
                fontSize: "15px",
                letterSpacing: "0.3px",
              }}
            >
              Activity Launcher
            </Box>
          </Box>

          <SideActionBar
            fullWidth
            onClick={onPatientManagementClick}
            iconSrc="/assets/images/newIcons/PM.svg"
            label="Patient Registration"
            ariaLabel="open patient registration"
            theme="blue"
          />

          {showAnalytics && (
            <SideActionBar
              fullWidth
              onClick={onAnalyticsClick}
              iconSrc="/assets/images/newIcons/RAA.svg"
              label="Analytics"
              ariaLabel="open analytics"
              theme="teal"
            />
          )}

          <Box
            sx={{
              p: 2,
              borderRadius: 0,
              borderRight: "1px solid #21cdc0",
            }}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 1.5,
                alignContent: "start",
              }}
            >
              {navItems.map((item) => {
                const isActive = activeSection === item.section;
                const NavIcon = item.icon;
                return (
                  <Box
                    key={item.section}
                    onClick={() => handleNavItemClick(item.section)}
                    sx={{
                      aspectRatio: "1 / 1",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      cursor: "pointer",
                      p: 1.5,
                      borderRadius: "12px",
                      border: "1px solid #21cdc0",
                      backgroundColor: isActive ? "transparent" : "#FFFFFF",
                      background: isActive
                        ? "linear-gradient(195.29deg, #64E0D9 10.74%, #114873 95.14%)"
                        : "#FFFFFF",
                      color: isActive ? "#FFFFFF" : "#121959",
                      transition: "all 0.3s ease",
                      boxShadow: isActive
                        ? "0px 6px 18px rgba(17, 72, 115, 0.28)"
                        : "0px 2px 8px rgba(0, 0, 0, 0.06)",
                      "&:hover": {
                        background:
                          "linear-gradient(195.29deg, #64E0D9 10.74%, #114873 95.14%)",
                        color: "#FFFFFF",
                        transform: "translateY(-4px)",
                        boxShadow: "0px 8px 22px rgba(17, 72, 115, 0.25)",
                        "& .nav-icon": { color: "#FFFFFF !important" },
                        "& .nav-label": { color: "#FFFFFF !important" },
                      },
                    }}
                  >
                    <NavIcon
                      className="nav-icon"
                      sx={{
                        fontSize: 30,
                        color: isActive ? "#FFFFFF" : "#21cdc0",
                        transition: "color 0.3s ease",
                      }}
                    />
                    <Typography
                      className="nav-label"
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        lineHeight: 1.2,
                        color: isActive ? "#FFFFFF" : "#121959",
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}
      </Box>

      {!open && (
        <>
          <SideActionBar
            onClick={onPatientManagementClick}
            iconSrc="/assets/images/newIcons/PM.svg"
            label="Patient Registration"
            ariaLabel="open patient registration"
            theme="blue"
          />
          {showAnalytics && (
            <SideActionBar
              onClick={onAnalyticsClick}
              iconSrc="/assets/images/newIcons/RAA.svg"
              label="Analytics"
              ariaLabel="open analytics"
              theme="teal"
            />
          )}
        </>
      )}
    </Box>
  );
}
